'use client';

import Link from '@/components/NoPrefetchLink';
import { useEffect, useMemo, useState } from 'react';
import type { NpbAwardWinnersResponse, NpbAwardWinner } from '@/lib/types';
import { fetchNpbAwardWinners } from '@/lib/apiClient';
import { withLocalePrefix } from '@/lib/localePath';
import type { Locale } from '@/lib/localePath';
import { translateNpbAwardDivision, translateNpbAwardLeague, translateNpbAwardTeam, translateNpbPosition } from '@/lib/format';
import { ScrollToTopButton } from './ScrollToTopButton';
import '@/styles/InfoPage.css';

type Props = { locale: Locale; awardId: string; apiAwardId: string; initialData?: NpbAwardWinnersResponse; focusYear?: number | null };
type NpbLeagueTab = { id: 'npb-two-leagues' | 'npb-one-league'; label: string; leagues: string[] };

const awardSlug = (awardId: string): string => awardId.replace(/ /g, '-');

export default function NpbAwardDetailClient({ locale, awardId: rawAwardId, apiAwardId, initialData, focusYear: focusYearProp = null }: Props) {
    const [data, setData] = useState<NpbAwardWinnersResponse | null>(initialData ?? null);
    const [loading, setLoading] = useState(!initialData);
    const [notFound, setNotFound] = useState(false);
    const [activeLeagueTabId, setActiveLeagueTabId] = useState<NpbLeagueTab['id'] | null>(null);
    const [urlFocusYear, setUrlFocusYear] = useState<number | null>(null);
    const focusYear = focusYearProp ?? urlFocusYear;

    const t = (ja: string, en: string, es: string) =>
        locale === 'en' ? en : locale === 'es' ? es : ja;

    const playerName = (winner: NpbAwardWinner) => {
        if (locale === 'ja') return winner.name;
        return winner.nameEn || winner.name;
    };

    const awardTitle = (res: NpbAwardWinnersResponse) =>
        locale === 'en'
            ? (res.awardNameEn || res.awardId)
            : locale === 'es'
            ? (res.awardNameEs || res.awardNameEn || res.awardId)
            : (res.awardNameJa || res.awardId);

    const fallbackAwardTitle = useMemo(() => {
        if (!rawAwardId) return t('プロ野球（NPB）アワード', 'Nippon Professional Baseball (NPB) Awards', 'Premios de Béisbol Profesional Japonés (NPB)');
        try { return decodeURIComponent(rawAwardId).replace(/-/g, ' '); }
        catch { return rawAwardId.replace(/-/g, ' '); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawAwardId, locale]);

    useEffect(() => {
        if (initialData) {
            setData(initialData);
            setLoading(false);
            setNotFound(false);
            return;
        }

        const controller = new AbortController();
        setLoading(true);
        setNotFound(false);
        fetchNpbAwardWinners(apiAwardId, undefined, undefined, controller.signal)
            .then((res) => {
                if (!res) {
                    setNotFound(true);
                    return;
                }
                setData(res);
                setNotFound(false);
            })
            .catch((error: unknown) => {
                if (error instanceof Error && error.name === 'AbortError') return;
                setNotFound(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [apiAwardId, initialData]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setUrlFocusYear(parseFocusYear(params.get('year') ?? params.get('awardYear')));
    }, []);

    const normalizeLeague = (league: string | null | undefined): string => {
        const raw = (league ?? '').trim();
        const upper = raw.toUpperCase();
        if (raw === 'セントラル' || upper === 'CENTRAL') return 'セントラル';
        if (raw === 'パシフィック' || upper === 'PACIFIC') return 'パシフィック';
        if (upper === 'NPB') return 'NPB';
        return raw;
    };

    const LEAGUE_ORDER = ['セントラル', 'パシフィック', 'NPB'];
    const allLeagues = data ? Array.from(new Set(data.winners.map((w) => normalizeLeague(w.league)))) : [];
    const hasLeague = allLeagues.some((l) => l !== '');
    const leagues = hasLeague
        ? allLeagues.filter((l) => l !== '').sort((a, b) => {
            const ia = LEAGUE_ORDER.indexOf(a);
            const ib = LEAGUE_ORDER.indexOf(b);
            if (ia !== -1 && ib !== -1) return ia - ib;
            if (ia !== -1) return -1;
            if (ib !== -1) return 1;
            return a.localeCompare(b);
        })
        : [''];

    const leagueTabs = useMemo<NpbLeagueTab[]>(() => {
        if (!hasLeague) {
            return [{
                id: 'npb-one-league',
                label: t('受賞者', 'Winner', 'Ganador'),
                leagues: [''],
            }];
        }

        const twoLeagues = leagues.filter((lg) => lg === 'セントラル' || lg === 'パシフィック');
        const oneLeague = leagues.filter((lg) => lg !== 'セントラル' && lg !== 'パシフィック');
        const tabs: NpbLeagueTab[] = [];

        if (twoLeagues.length > 0) {
            tabs.push({
                id: 'npb-two-leagues',
                label: t(
                    'セントラルリーグ / パシフィックリーグ',
                    'Central League / Pacific League',
                    'Liga Central / Liga del Pacífico',
                ),
                leagues: twoLeagues,
            });
        }
        if (oneLeague.length > 0) {
            tabs.push({
                id: 'npb-one-league',
                label: t(
                    'プロ野球（NPB）（1リーグ）',
                    'Nippon Professional Baseball (NPB) (One League)',
                    'Béisbol Profesional Japonés (NPB) (Liga Única)',
                ),
                leagues: oneLeague,
            });
        }
        return tabs.length > 0 ? tabs : [{
            id: 'npb-one-league',
            label: t('受賞者', 'Winner', 'Ganador'),
            leagues: [''],
        }];
    }, [hasLeague, leagues, locale]);

    useEffect(() => {
        if (activeLeagueTabId && !leagueTabs.some((tab) => tab.id === activeLeagueTabId)) {
            setActiveLeagueTabId(null);
        }
    }, [leagueTabs, activeLeagueTabId]);

    const preferredLeagueTabId = (() => {
        if (focusYear && data) {
            const targetTab = leagueTabs.find((tab) =>
                data.winners.some((winner) =>
                    winner.year === focusYear && tab.leagues.includes(normalizeLeague(winner.league)),
                ),
            );
            if (targetTab) return targetTab.id;
        }
        return leagueTabs[0]?.id ?? 'npb-two-leagues';
    })();
    const activeLeagueTab = leagueTabs.find((tab) => tab.id === (activeLeagueTabId ?? preferredLeagueTabId)) ?? leagueTabs[0] ?? null;
    const visibleLeagues = activeLeagueTab?.leagues ?? [''];

    const translateLeague = (lg: string) => {
        return translateNpbAwardLeague(lg, locale);
    };

    useEffect(() => {
        if (!focusYear || !data) return;
        const frame = window.requestAnimationFrame(() => {
            const target = document.getElementById(`award-year-${focusYear}`);
            if (!target) return;
            const headerEl = document.querySelector('.app-header') as HTMLElement | null;
            const headerHeight = headerEl?.getBoundingClientRect().height ?? 0;
            const awardHeaderEl = document.querySelector('.award-detail-sticky-header') as HTMLElement | null;
            const awardHeaderHeight = awardHeaderEl?.getBoundingClientRect().height ?? 0;
            const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - awardHeaderHeight - 12;
            window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        });
        return () => window.cancelAnimationFrame(frame);
    }, [focusYear, data, activeLeagueTab?.id]);

    const formatMonthName = (month: string): string => {
        const date = new Date(2000, Number(month) - 1, 1);
        return date.toLocaleString(locale === 'es' ? 'es-ES' : 'en-US', { month: 'short' });
    };

    const formatPeriod = (winner: NpbAwardWinner): string => {
        const label = winner.periodLabel;
        if (!label) return '-';
        const monthRange = label.match(/^(\d{4})-(\d{2})_(\d{2})$/);
        if (monthRange) {
            const [, , m1, m2] = monthRange;
            return locale === 'ja' ? `${Number(m1)}・${Number(m2)}月` : `${formatMonthName(m1)}/${formatMonthName(m2)}`;
        }
        const month = label.match(/^(\d{4})-(\d{2})$/);
        if (month) {
            const [, , m] = month;
            return locale === 'ja' ? `${Number(m)}月` : formatMonthName(m);
        }
        return label;
    };

    if (loading) {
        return <div className="info-page"><p>{t('読み込み中...', 'Loading...', 'Cargando...')}</p></div>;
    }

    if (notFound || !data) {
        return (
            <div className="info-page">
                <p>{t('アワードが見つかりませんでした。', 'Award not found.', 'Premio no encontrado.')}</p>
            </div>
        );
    }

    const NPB_POSITION_ORDER = ['投手', '捕手', '一塁手', '二塁手', '三塁手', '遊撃手', '外野手', '指名打者'];
    const npbPositionRank = (pos: string | null | undefined) => {
        const idx = NPB_POSITION_ORDER.indexOf(pos || '');
        return idx === -1 ? 99 : idx;
    };
    const npbLeagueRank = (league: string | null | undefined) => {
        const idx = LEAGUE_ORDER.indexOf(normalizeLeague(league));
        return idx === -1 ? 99 : idx;
    };

    const formatLeagueDetail = (winner: NpbAwardWinner): string => {
        const normalizedLeague = normalizeLeague(winner.league);
        const league = normalizedLeague ? translateLeague(normalizedLeague) : '';
        const division = winner.division ? translateNpbAwardDivision(winner.division, locale) : '';
        const position = winner.position ? translateNpbPosition(winner.position, locale) : '';
        const parts = normalizedLeague === 'ファーム' && division
            ? [division, position]
            : [league, division, position];
        const label = parts.filter((part) => part && part !== '-').join(' / ');
        return label || '-';
    };

    const detailRows = data.winners
        .filter((winner) => visibleLeagues.some((league) => normalizeLeague(winner.league) === league))
        .sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            const periodCmp = (b.periodEnd || '').localeCompare(a.periodEnd || '');
            if (periodCmp !== 0) return periodCmp;
            const leagueCmp = npbLeagueRank(a.league) - npbLeagueRank(b.league);
            if (leagueCmp !== 0) return leagueCmp;
            const positionCmp = npbPositionRank(a.position) - npbPositionRank(b.position);
            return positionCmp !== 0 ? positionCmp : 0;
        });
    const showPeriodColumn = detailRows.some((winner) =>
        Boolean(winner.periodLabel?.trim() || winner.periodStart?.trim() || winner.periodEnd?.trim()),
    );
    const showTeamColumn = detailRows.some((winner) => Boolean(winner.teamName?.trim()));
    const yearRowSpans = detailRows.reduce<Map<number, number>>((acc, winner) => {
        acc.set(winner.year, (acc.get(winner.year) ?? 0) + 1);
        return acc;
    }, new Map<number, number>());

    return (
        <div className="info-page">
            <div className="award-detail-sticky-header">
                <nav className="awards-breadcrumb">
                    <Link href={withLocalePrefix(locale, '/')}>{t('ホーム', 'Home', 'Inicio')}</Link>
                    {' › '}
                    <Link href={withLocalePrefix(locale, '/npb-awards')}>
                        {t('プロ野球（NPB）アワード一覧', 'Nippon Professional Baseball (NPB) Awards', 'Premios de Béisbol Profesional Japonés (NPB)')}
                    </Link>
                    {' › '}
                    <span>{awardTitle(data)}</span>
                </nav>

                <h1>{awardTitle(data)}</h1>
            </div>

            {leagueTabs.length > 1 && (
                <div
                    className="info-tabs"
                    role="tablist"
                    aria-label={t(
                        'NPBリーグ表示切替',
                        'Switch NPB league groups',
                        'Cambiar grupos de liga NPB',
                    )}
                >
                    {leagueTabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={activeLeagueTab?.id === tab.id}
                            className={`info-tab-button ${activeLeagueTab?.id === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveLeagueTabId(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="awards-table-wrapper">
                <table className="stats-table awards-stats-table">
                    <thead>
                        <tr>
                            <th>{t('年', 'Year', 'Año')}</th>
                            {showPeriodColumn && <th>{t('月', 'Month', 'Mes')}</th>}
                            <th>{t('リーグ', 'League', 'Liga')}</th>
                            <th>{t('選手名', 'Player', 'Jugador')}</th>
                            {showTeamColumn && <th>{t('チーム名', 'Team', 'Equipo')}</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {detailRows.map((winner, index) => {
                            const showYear = index === 0 || detailRows[index - 1].year !== winner.year;
                            return (
                                <tr key={`${winner.year}-${winner.periodLabel || ''}-${winner.league || ''}-${winner.division || ''}-${winner.name}-${index}`} id={showYear ? `award-year-${winner.year}` : undefined}>
                                    {showYear && (
                                        <td className="numeric" rowSpan={yearRowSpans.get(winner.year) ?? 1}>{winner.year}</td>
                                    )}
                                    {showPeriodColumn && <td>{formatPeriod(winner)}</td>}
                                    <td>{formatLeagueDetail(winner)}</td>
                                    <td>
                                        {winner.slug ? (
                                            <Link href={`${withLocalePrefix(locale, `/players/${winner.slug}`)}?from=npb-awards&awardSlug=${rawAwardId}&awardNameEn=${encodeURIComponent(data.awardNameEn || data.awardId)}&awardNameJa=${encodeURIComponent(data.awardNameJa || data.awardId)}&awardNameEs=${encodeURIComponent(data.awardNameEs || data.awardNameEn || data.awardId)}`}>
                                                {playerName(winner)}
                                            </Link>
                                        ) : playerName(winner)}
                                    </td>
                                    {showTeamColumn && <td>{winner.teamName ? translateNpbAwardTeam(winner.teamName, locale) : '-'}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <ScrollToTopButton />
        </div>
    );
}

const parseFocusYear = (value: string | null): number | null => {
    const year = value ? Number(value) : NaN;
    return Number.isInteger(year) && year > 0 ? year : null;
};
